import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import type {
  KeywordScoringFieldDef,
  KeywordScoringResponse,
  KeywordScoringValue,
} from '../types';

/**
 * Your rules for judging keywords: when one gets the ★, and when a result on
 * page one counts as open. The keyword lists, the "N open" badge and recon's
 * "Where to win" all read these. Only what you change is stored; everything
 * else stays at the default shown beside it.
 */

type Draft = Record<string, KeywordScoringValue | null>;

/** A value equal to the default is stored as "use the default". */
export function toOverride(
  draft: Draft,
  builtin: Record<string, KeywordScoringValue>,
): Draft {
  const out: Draft = {};
  for (const [k, v] of Object.entries(draft)) {
    out[k] = v === null || v === undefined || v === builtin[k] ? null : v;
  }
  return out;
}

function show(v: KeywordScoringValue): string {
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (typeof v === 'number') return v.toLocaleString();
  return v.toLowerCase();
}

export default function KeywordScoringSettings() {
  const [data, setData] = useState<KeywordScoringResponse | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = (res: KeywordScoringResponse) => {
    setData(res);
    setDraft({ ...res.override });
  };

  useEffect(() => {
    let active = true;
    api
      .getKeywordScoringSettings()
      .then((res) => active && load(res))
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      active = false;
    };
  }, []);

  const customized = useMemo(
    () => !!data && Object.values(data.override).some((v) => v !== null),
    [data],
  );

  const set = (key: string, value: KeywordScoringValue | null) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setFlash(null);
    setError(null);
  };

  const run = async (call: () => Promise<KeywordScoringResponse>, done: string) => {
    setSaving(true);
    setError(null);
    setFlash(null);
    try {
      load(await call());
      setFlash(done);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 py-6">
        {error ?? 'Loading…'}
      </div>
    );
  }

  const field = (f: KeywordScoringFieldDef) => {
    const def = data.builtin[f.key];
    const value = draft[f.key];
    const current = value ?? def;
    const input =
      'w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-blue-500';
    if (f.kind === 'toggle') {
      return (
        <label key={f.key} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={current === true}
            onChange={(e) => set(f.key, e.target.checked)}
          />
          <span>
            {f.label}
            <span className="block text-[11px] text-gray-400 dark:text-gray-500">default {show(def)}</span>
          </span>
        </label>
      );
    }
    return (
      <label key={f.key} className="block">
        <span className="block text-xs text-gray-700 dark:text-gray-300 mb-0.5">{f.label}</span>
        {f.kind === 'choice' ? (
          <select
            className={input}
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(e) => set(f.key, e.target.value || null)}
          >
            <option value="">Default ({show(def)})</option>
            {(f.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o.charAt(0) + o.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="number"
            min={f.min}
            max={f.max}
            className={input}
            value={value === null || value === undefined ? '' : String(value)}
            placeholder={String(def)}
            onChange={(e) => set(f.key, e.target.value === '' ? null : Number(e.target.value))}
          />
        )}
        <span className="block text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">default {show(def)}</span>
      </label>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Keyword scoring</h2>
          {customized && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
              Customized
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          When a keyword gets the ★, and when a result on page one counts as open. The keyword lists, the
          &ldquo;N open&rdquo; badge and recon&rsquo;s &ldquo;Where to win&rdquo; all use these. Changes show the next time a
          list loads; nothing needs to be searched again.
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}
      {flash && (
        <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded px-3 py-2">
          {flash}
        </div>
      )}

      {data.field_groups.map((group) => (
        <section
          key={group.key}
          className="border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"
        >
          <header className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
            <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{group.label}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{group.description}</p>
          </header>
          <div className="px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {group.fields.map(field)}
          </div>
        </section>
      ))}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={saving}
          onClick={() =>
            run(
              () => api.updateKeywordScoringSettings(toOverride(draft, data.builtin)),
              'Saved',
            )
          }
          className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {customized && (
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (window.confirm('Put every keyword setting back to its default?')) {
                void run(() => api.resetKeywordScoringSettings(), 'Back to the defaults');
              }
            }}
            className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
          >
            Reset all to defaults
          </button>
        )}
      </div>
    </div>
  );
}
